import React from 'react';
import { Link } from 'react-router-dom';
import { FileText, Download, Calculator, TrendingUp, HelpCircle } from 'lucide-react';
import { useSEO } from '../hooks/useSEO';
import FAQStructuredData from '../components/FAQStructuredData';

const Spreadsheets: React.FC = () => {
  useSEO({
    title: 'Planilhas de Controle Financeiro e Orçamento Familiar Grátis (Excel 2026) | O Teu Dinheiro',
    description: 'Baixe gratuitamente as melhores planilhas de orçamento familiar e controle de gastos mensais em Excel (.xlsx) do SEBRAE e Sicoob. Prontas para usar em 2026.',
    keywords: 'planilha controle financeiro gratis, planilha orçamento familiar excel, planilha de gastos mensais, planilha sebrae, planilha sicoob, baixar planilha financeira 2026',
    url: 'https://www.oteudinheiro.com.br/planilhas'
  });

  const faqItems = [
    {
      question: 'Qual a melhor planilha gratuita para controle financeiro pessoal e familiar?',
      answer: 'A planilha de Orçamento Familiar do SEBRAE é ideal para quem quer detalhar todas as categorias de receitas e despesas mensais, enquanto a planilha do Sicoob é excelente para visão anual, quitação de dívidas e formação de reserva de emergência.'
    },
    {
      question: 'As planilhas funcionam no Google Planilhas (Google Sheets) e no celular?',
      answer: 'Sim! Os arquivos estão no formato .xlsx (Excel), que pode ser aberto gratuitamente no Google Planilhas (Google Sheets), LibreOffice ou Microsoft Excel tanto no computador quanto no aplicativo de celular.'
    },
    {
      question: 'Preciso pagar ou cadastrar cartão para baixar as planilhas?',
      answer: 'Não. O download das planilhas no portal O Teu Dinheiro é 100% gratuito, direto e sem necessidade de cadastro.'
    }
  ];
  const spreadsheets = [
    {
      name: 'Orçamento Familiar - SEBRAE',
      description: 'Planilha completa do SEBRAE para controle financeiro familiar com categorias detalhadas',
      icon: <Calculator className="w-8 h-8" />,
      features: ['Categorização de receitas e despesas', 'Controle mensal', 'Análise de gastos', 'Metas financeiras'],
      downloadUrl: '/assets/sheets/Orçamento Familiar - SEBRAE.xlsx',
      size: 'Excel (.xlsx)',
      source: 'SEBRAE'
    },
    {
      name: 'Orçamento Familiar - Sicoob',
      description: 'Planilha do Sicoob para planejamento financeiro familiar com foco em educação financeira',
      icon: <TrendingUp className="w-8 h-8" />,
      features: ['Planejamento anual', 'Controle de dívidas', 'Reserva de emergência', 'Educação financeira'],
      downloadUrl: '/assets/sheets/Orçamento Familiar - Sicoob.xlsx',
      size: 'Excel (.xlsx)',
      source: 'Sicoob'
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <FAQStructuredData items={faqItems} />
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-rose-600 to-rose-700 text-white py-20">
        <div className="container-custom text-center">
          <FileText className="w-16 h-16 mx-auto mb-6" />
          <h1 className="text-4xl md:text-5xl font-bold mb-6">
            Planilhas Gratuitas
          </h1>
          <p className="text-xl md:text-2xl text-rose-100 max-w-3xl mx-auto">
            Baixe planilhas gratuitas do SEBRAE e Sicoob para organizar suas finanças familiares
          </p>
        </div>
      </section>

      {/* Main Content */}
      <section className="section-padding">
        <div className="container-custom">
          <h2 className="text-3xl font-bold text-gray-900 text-center mb-6">
            Planilhas disponíveis para download
          </h2>
          <p className="text-center text-gray-600 mb-12 max-w-2xl mx-auto">
            Planilhas oficiais desenvolvidas por instituições reconhecidas para ajudar no controle financeiro familiar
          </p>

          <div className="grid md:grid-cols-2 gap-8">
            {spreadsheets.map((spreadsheet, index) => (
              <div key={index} className="bg-white rounded-xl shadow-lg p-6 hover:shadow-xl transition-shadow">
                <div className="flex items-start space-x-4 mb-4">
                  <div className="bg-rose-100 p-3 rounded-lg">
                    {spreadsheet.icon}
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="text-xl font-bold text-gray-900">{spreadsheet.name}</h3>
                      <span className="bg-blue-100 text-blue-600 px-2 py-1 rounded-full text-xs font-medium">
                        {spreadsheet.source}
                      </span>
                    </div>
                    <p className="text-gray-600 mb-3">{spreadsheet.description}</p>

                    <div className="flex items-center justify-between mb-4">
                      <span className="text-sm text-gray-500">Formato: {spreadsheet.size}</span>
                      <a
                        href={spreadsheet.downloadUrl}
                        download
                        className="btn bg-rose-600 text-white hover:bg-rose-700 text-sm px-4 py-2"
                      >
                        <Download className="w-4 h-4 mr-2" />
                        Baixar Gratuito
                      </a>
                    </div>
                  </div>
                </div>

                <div className="border-t border-gray-200 pt-4">
                  <h4 className="font-semibold text-gray-900 mb-2">Funcionalidades:</h4>
                  <ul className="text-sm text-gray-600 space-y-1">
                    {spreadsheet.features.map((feature, featureIndex) => (
                      <li key={featureIndex} className="flex items-center">
                        <span className="w-1.5 h-1.5 bg-rose-500 rounded-full mr-2"></span>
                        {feature}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How to Use Section */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <h2 className="text-3xl font-bold text-gray-900 text-center mb-12">
            Como usar as planilhas
          </h2>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="bg-rose-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl font-bold text-rose-600">1</span>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">
                Baixe a planilha
              </h3>
              <p className="text-gray-600">
                Escolha a planilha que atende suas necessidades e faça o download gratuito.
              </p>
            </div>

            <div className="text-center">
              <div className="bg-rose-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl font-bold text-rose-600">2</span>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">
                Personalize
              </h3>
              <p className="text-gray-600">
                Adapte as categorias, valores e metas para sua situação financeira específica.
              </p>
            </div>

            <div className="text-center">
              <div className="bg-rose-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl font-bold text-rose-600">3</span>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">
                Use regularmente
              </h3>
              <p className="text-gray-600">
                Preencha diariamente ou semanalmente para manter o controle sempre atualizado.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Tips Section */}
      <section className="section-padding">
        <div className="container-custom">
          <h2 className="text-3xl font-bold text-gray-900 text-center mb-12">
            Dicas para usar as planilhas
          </h2>

          <div className="grid md:grid-cols-2 gap-8">
            <div className="tip-box">
              <h3 className="text-xl font-semibold text-secondary-900 mb-3">
                Seja consistente
              </h3>
              <p className="text-secondary-700">
                Use as planilhas regularmente para ter dados precisos e
                conseguir identificar padrões nos seus gastos.
              </p>
            </div>

            <div className="tip-box">
              <h3 className="text-xl font-semibold text-secondary-900 mb-3">
                Personalize conforme necessário
              </h3>
              <p className="text-secondary-700">
                Adapte as categorias e campos para sua realidade.
                Não existe uma solução única para todos.
              </p>
            </div>

            <div className="tip-box">
              <h3 className="text-xl font-semibold text-secondary-900 mb-3">
                Faça backup regular
              </h3>
              <p className="text-secondary-700">
                Salve suas planilhas na nuvem ou faça backup regular
                para não perder seus dados importantes.
              </p>
            </div>

            <div className="tip-box">
              <h3 className="text-xl font-semibold text-secondary-900 mb-3">
                Analise os dados
              </h3>
              <p className="text-secondary-700">
                Não apenas preencha, mas analise os gráficos e relatórios
                para tomar decisões financeiras mais inteligentes.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="section-padding bg-white border-t border-gray-100">
        <div className="container-custom max-w-4xl">
          <div className="flex items-center justify-center gap-3 mb-8">
            <HelpCircle className="w-8 h-8 text-rose-600" />
            <h2 className="text-3xl font-bold text-gray-900 text-center">
              Perguntas Frequentes sobre as Planilhas
            </h2>
          </div>
          <div className="space-y-4">
            {faqItems.map((item, idx) => (
              <div key={idx} className="bg-gray-50 p-6 rounded-xl border border-gray-200">
                <h3 className="text-lg font-bold text-gray-900 mb-2">{item.question}</h3>
                <p className="text-gray-700 text-sm leading-relaxed">{item.answer}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding bg-rose-600 text-white">
        <div className="container-custom text-center">
          <h2 className="text-3xl font-bold mb-6">
            Comece a organizar suas finanças hoje!
          </h2>
          <p className="text-xl text-rose-100 mb-8 max-w-2xl mx-auto">
            As planilhas são gratuitas e podem transformar sua relação com o dinheiro.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/calculadoras"
              className="btn bg-white text-rose-600 hover:bg-gray-100"
            >
              Usar Calculadoras
            </Link>
            <Link
              to="/planejamento"
              className="btn bg-transparent text-white border-2 border-white hover:bg-white hover:text-rose-600"
            >
              Ver Planejamento
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Spreadsheets;
